import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import axios from 'axios';
import { withTranslation } from 'react-i18next';
import DecimalField from '../shared/DecimalField';
import { formatPriceWithCurrency } from '../../utils/vat';

const MAX_FEE = 12;

/** ISO date from the API -> d.m.yyyy, the only date format a Finnish workshop reads. */
const formatDate = (iso) => {
  if (!iso) return '';
  const [year, month, day] = String(iso).split('-');
  return year && month && day ? `${Number(day)}.${Number(month)}.${year}` : String(iso);
};

/**
 * Maksumuistutus dialog.
 *
 * The amounts come from the backend draft rather than being computed here: the ten-day
 * fee rule of Perintälaki 10 f § and the twelve euro cap of 10 e § are decided on the
 * server, and a second opinion in the browser would only be able to disagree with it.
 *
 * Nothing in here books anything. The fee is printed on the reminder; the invoice keeps
 * tracking its principal alone, which is what the note under the totals says out loud so
 * the user is not left guessing what the extra line did to their receivables.
 */
function PaymentReminderDialog({ t, open, invoice, onClose, onSent, onNotify }) {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [fee, setFee] = useState('0.00');
  const [delivery, setDelivery] = useState('email');
  const [email, setEmail] = useState('');

  const invoiceId = invoice?.id;

  const load = useCallback(async () => {
    if (!invoiceId) return;
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`/api/invoices/${invoiceId}/payment-reminder/`);
      setData(response.data);
      setEmail(response.data.default_recipient || '');
      setFee(response.data.draft?.fee_amount ?? '0.00');
      setDelivery(response.data.default_recipient ? 'email' : 'print');
    } catch (err) {
      setError(err.response?.data?.error || t('invoices.paymentReminderFailed'));
    } finally {
      setLoading(false);
    }
  }, [invoiceId, t]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const draft = data?.draft;
  const feeSuppressed = !!draft?.fee_suppressed_reason;
  const feeValue = feeSuppressed ? 0 : (parseFloat(fee) || 0);
  const principalValue = parseFloat(draft?.principal ?? 0) || 0;
  const language = data?.language || 'fi';

  const handlePreview = () => {
    window.open(
      `/api/invoices/${invoiceId}/payment-reminder-pdf/?preview=true&language=${language}`,
      '_blank'
    );
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError('');
    try {
      const response = await axios.post(`/api/invoices/${invoiceId}/payment-reminder/`, {
        delivery,
        fee: feeSuppressed ? undefined : fee,
        email: delivery === 'email' ? email : undefined,
        language,
      });
      if (delivery === 'print') {
        window.open(
          `/api/invoices/${invoiceId}/payment-reminder-pdf/?reminder=${response.data.id}&preview=true&language=${language}`,
          '_blank'
        );
      }
      onNotify?.(
        delivery === 'email'
          ? t('invoices.paymentReminderSentEmail')
          : t('invoices.paymentReminderCreated'),
        'success'
      );
      onSent?.(response.data);
      onClose?.();
    } catch (err) {
      setError(err.response?.data?.error || t('invoices.paymentReminderFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit =
    !!draft && !submitting && (delivery !== 'email' || !!email);

  return (
    <Dialog open={!!open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {t('invoices.paymentReminderTitle', { number: invoice?.invoice_number || '' })}
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : !data?.eligible ? (
          // A refusal is information ("not overdue yet"); a failed load is an error.
          <Alert severity={data ? 'info' : 'error'}>{data?.reason || error}</Alert>
        ) : (
          <Stack spacing={2}>
            {error && <Alert severity="error">{error}</Alert>}

            <Typography variant="body2" color="text.secondary">
              {t('invoices.paymentReminderSequence', { n: draft.sequence })}
              {' · '}
              {t('invoices.paymentReminderDaysOverdue', { days: draft.days_overdue })}
            </Typography>

            <Box>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2">{t('invoices.paymentReminderPrincipal')}</Typography>
                <Typography variant="body2">{formatPriceWithCurrency(principalValue)}</Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2">{t('invoices.paymentReminderFee')}</Typography>
                <Typography variant="body2">{formatPriceWithCurrency(feeValue)}</Typography>
              </Stack>
              <Divider sx={{ my: 1 }} />
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="subtitle2">{t('invoices.paymentReminderTotal')}</Typography>
                <Typography variant="subtitle2">
                  {formatPriceWithCurrency(principalValue + feeValue)}
                </Typography>
              </Stack>
              <Typography variant="caption" color="text.secondary">
                {t('invoices.paymentReminderNoteNotBooked')}
              </Typography>
            </Box>

            <DecimalField
              label={t('invoices.paymentReminderFee')}
              value={feeSuppressed ? '0.00' : fee}
              onChange={setFee}
              max={MAX_FEE}
              size="small"
              disabled={feeSuppressed}
              helperText={
                feeSuppressed
                  ? t('invoices.paymentReminderFeeSuppressed')
                  : t('invoices.paymentReminderFeeMax')
              }
            />

            <Typography variant="body2">
              {t('invoices.paymentReminderNewDueDate')}: {formatDate(draft.new_due_date)}
            </Typography>

            <Typography variant="caption" color="text.secondary">
              {t('invoices.paymentReminderInterestNote')}
            </Typography>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                {t('invoices.paymentReminderDelivery')}
              </Typography>
              <RadioGroup
                row
                value={delivery}
                onChange={(event) => setDelivery(event.target.value)}
              >
                <FormControlLabel
                  value="email"
                  control={<Radio size="small" />}
                  label={t('invoices.paymentReminderEmail')}
                />
                <FormControlLabel
                  value="print"
                  control={<Radio size="small" />}
                  label={t('invoices.paymentReminderPrint')}
                />
              </RadioGroup>
            </Box>

            {delivery === 'email' && (
              <TextField
                fullWidth
                size="small"
                type="email"
                label={t('invoices.paymentReminderRecipient')}
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            )}

            {data.reminders?.length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  {t('invoices.paymentReminderHistory')}
                </Typography>
                {data.reminders.map((reminder) => (
                  <Typography key={reminder.id} variant="body2" color="text.secondary">
                    {t('invoices.paymentReminderSequence', { n: reminder.sequence })}
                    {' · '}
                    {formatDate(reminder.reminder_date)}
                    {' · '}
                    {reminder.delivery === 'email'
                      ? t('invoices.paymentReminderEmail')
                      : t('invoices.paymentReminderPrint')}
                    {' · '}
                    {formatPriceWithCurrency(parseFloat(reminder.total_amount) || 0)}
                  </Typography>
                ))}
              </Box>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('Cancel')}</Button>
        {data?.eligible && (
          <Button onClick={handlePreview} disabled={submitting}>
            {t('invoices.paymentReminderPreview')}
          </Button>
        )}
        {data?.eligible && (
          <Button variant="contained" onClick={handleSubmit} disabled={!canSubmit}>
            {delivery === 'email'
              ? t('invoices.paymentReminderSend')
              : t('invoices.paymentReminderCreate')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default withTranslation()(PaymentReminderDialog);
